import { getServiceBaseUrl } from '../config/api';
import { API_CONFIG } from '../config/api';
import { API_ENDPOINTS } from '../config/apiEndpoints';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getToken = () => localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);

// Export the Resume interface
export interface Resume {
  id: string;
  candidateName: string;
  candidateEmail?: string;
  email?: string;
  phone?: string;
  jobId: string;
  jobTitle?: string;
  status: string;
  score?: number;
  uploadedDate?: string;
  createdAt?: string;
  assignedSME?: string;
  filePath?: string;
  fileName?: string;
  resume_id?: string;
  candidate_name?: string;
  candidate_email?: string;
  job_id?: string;
  job_title?: string;
  resume_status?: string;
  uploaded_date?: string;
  assigned_sme?: string;
  file_path?: string;
  file_name?: string;
  created_at?: string;
}

export interface InterviewPanel {
  id: string;
  name: string;
  members: string[];
}

export interface ResumeListApiResponse {
  items: Resume[];
  total: number;
  page: number;
  size: number;
  pages: number;
  resumes?: Resume[];
  data?: Resume[];
}

export interface CreateResumePayload {
  job_id: string;
  job_summary: string;
  job_description: string;
  panel_list: string[];
  resume_location: string;
  resume_summary: string;
  skills: string[];
  candidate_name: string;
  candidate_email: string;
  status: string;
  source: string;
  resume_file_size: number;
  resume_file_type: string;
  notes: string;
  priority_level: string;
  submission_date: string;
}

// Upload response interface
export interface UploadResponse {
  success: boolean;
  message: string;
  uploaded_files?: Array<{
    filename: string;
    resume_id: string;
    status: string;
  }>;
  failed_files?: Array<{
    filename: string;
    error: string;
  }>;
  total_uploaded?: number;
  total_failed?: number;
}

// Helper function to update resume status with API
const updateResumeStatusAPI = async (
  resumeId: string,
  status: string,
  reason?: string,
  notes?: string
) => {
  try {
    const token = getToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('Updating resume status:', { resumeId, status, reason, notes });

    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.UPDATE_STATUS}/${resumeId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify({
        status,
        reason: reason || '',
        notes: notes || '',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail ||
          errorData.message ||
          `Failed to update resume status: ${response.status}`
      );
    }

    const result = await response.json();
    console.log('Resume status update response:', result);

    return result;
  } catch (error) {
    console.error('Error updating resume status:', error);
    throw error;
  }
};

// assign panel to resume
const assignPanelToResumeAPI = async (
  resumeId: string,
  panelId: string,
  assignmentData: any
): Promise<any> => {
  const token = getToken();

  console.log('Assigning panel to resume:', { resumeId, panelId });
  console.log('Using token:', token?.substring(0, 20) + '...');

  if (!token) {
    throw new Error('No authentication token found');
  }

  try {
    console.log('Making API call to assign panel...');

    const requestBody = {
      panel_list: [panelId],
      resume_id: resumeId,
      interview_date_time: assignmentData.interviewDateTime,
      duration_minutes: assignmentData.duration,
      notes: assignmentData.notes,
      avature_feedback_link: assignmentData.avatureFeedbackLink,
    };

    console.log('Request body:', requestBody);

    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.ASSIGN_PANEL}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Assign panel response status:', response.status);

    // Log response headers for debugging
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('❌ Panel assignment failed:', response.status, errorText);

      // Handle specific error cases
      if (response.status === 404) {
        throw new Error(`Resume not found. Please verify the resume ID: ${resumeId}`);
      } else if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to assign panels.');
      } else {
        throw new Error(
          `Failed to assign panel: ${response.status} ${response.statusText} - ${errorText}`
        );
      }
    }

    const result = await response.json();
    console.log('✅ Panel assignment SUCCESS:', result);

    // Update resume status to feedback_pending after successful panel assignment
    try {
      await updateResumeStatusAPI(
        resumeId,
        'feedback_pending',
        'Panel assigned',
        'Resume status automatically updated after panel assignment'
      );
      console.log('Resume status updated to feedback_pending after panel assignment');
    } catch (statusError) {
      // Log the error but don't fail the main operation
      console.error('Failed to update resume status after panel assignment:', statusError);
    }

    return result;
  } catch (error) {
    console.log('💥 Panel assignment error:', error);
    throw error;
  }
};

export const resumeService = {
  // ✅ Get all resumes with conditional emailID parameter based on user role
  getAllResumes: async (
    params: {
      jobId?: string;
      page?: number;
      size?: number;
      status?: string;
      assignedSME?: string;
      emailID?: string;
      role?: string;
      type?: string;
    } = {}
  ) => {
    const token = getToken();

    // Get user role from localStorage or passed params
    const userRole = params.role || localStorage.getItem('user_role') || 'sme';

    console.log('User role for API call:', userRole);

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params.jobId) queryParams.append('job_id', params.jobId);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.size) queryParams.append('size', params.size.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.assignedSME) queryParams.append('assigned_sme', params.assignedSME);
    if (params.type) queryParams.append('type', params.type);

    // Only include emailID for non-HR users
    if (userRole !== 'hr' && userRole !== 'admin') {
      let currentUserEmail = params.emailID;

      if (!currentUserEmail) {
        // Try to get from localStorage
        currentUserEmail = localStorage.getItem('user_email');
      }

      if (!currentUserEmail) {
        // Try to get from stored user object
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          currentUserEmail = storedUser.email;
        } catch (e) {
          console.warn('Could not parse stored user data');
        }
      }

      console.log('Email for API call:', currentUserEmail);

      // Only include emailID if we have a valid email and user is not HR
      if (
        currentUserEmail &&
        currentUserEmail !== 'emailnotfound' &&
        currentUserEmail.includes('@')
      ) {
        queryParams.append('emailID', currentUserEmail);
      }
    } else {
      console.log('HR/Admin user - not sending emailID parameter');
    }

    const url = `${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?${queryParams.toString()}`;
    console.log('Fetching resumes with URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },

  // ✅ Get resumes by panelist email with proper emailID parameter
  getResumesByPanelistEmail: async (
    emailID: string,
    page: number = 1,
    size: number = 100
  ): Promise<ResumeListApiResponse> => {
    const token = getToken();

    try {
      console.log('=== Fetching Panelist Resumes ===');
      console.log('EmailID:', emailID);

      // Validate email format
      if (!emailID || !emailID.includes('@')) {
        throw new Error(`Invalid email format provided: "${emailID}"`);
      }

      // Build query parameters with emailID
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());
      queryParams.append('emailID', emailID);

      const url = `${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?${queryParams.toString()}`;
      console.log('Panelist resumes API URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `Failed to fetch panelist resumes: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      console.log('Panelist resumes response:', data);

      // Normalize response
      const normalizedResponse: ResumeListApiResponse = {
        items: (data.items || []).map((resume: any) => ({
          id: resume.resume_id || resume.id,
          candidateName: resume.candidate_name || resume.candidateName || 'Unknown Candidate',
          candidateEmail: resume.candidate_email || resume.candidateEmail || resume.email || '',
          email: resume.candidate_email || resume.candidateEmail || resume.email || '',
          phone: resume.phone || resume.phoneNumber || '',
          jobId: resume.job_id || resume.jobId || '',
          jobTitle: resume.job_title || resume.jobTitle || null,
          status: resume.resume_status || resume.status || 'pending',
          score: resume.score || 0,
          uploadedDate:
            resume.createdAt ||
            resume.created_at ||
            resume.uploadedDate ||
            new Date().toISOString(),
          assignedSME: resume.assigned_sme || resume.assignedSME || '',
          filePath: resume.file_path || resume.filePath || '',
          fileName: resume.file_name || resume.fileName || '',
          ...resume,
        })),
        total: data.total || data.total_count || (data.items || []).length,
        page: data.page || page,
        size: data.size || size,
        pages:
          data.pages ||
          Math.ceil((data.total || data.total_count || (data.items || []).length) / size),
      };

      return normalizedResponse;
    } catch (error) {
      console.error('Error fetching panelist resumes:', error);
      throw error;
    }
  },

  // Get paginated resumes (for compatibility with ResumeTablePage)
  getAllResumesPaginated: async (
    page: number = 1,
    size: number = 10
  ): Promise<ResumeListApiResponse> => {
    try {
      const response = await resumeService.getAllResumes({ page, size });

      console.log('Raw API response:', response);

      // Normalize the response to match ResumeListApiResponse interface
      let normalizedResponse: ResumeListApiResponse;

      if (response.items) {
        normalizedResponse = response;
      } else if (response.resumes) {
        normalizedResponse = {
          items: response.resumes,
          total: response.total || response.resumes.length,
          page: response.page || page,
          size: response.size || size,
          pages: response.pages || Math.ceil((response.total || response.resumes.length) / size),
        };
      } else if (response.data) {
        normalizedResponse = {
          items: response.data,
          total: response.total || response.data.length,
          page: response.page || page,
          size: response.size || size,
          pages: response.pages || Math.ceil((response.total || response.data.length) / size),
        };
      } else if (Array.isArray(response)) {
        normalizedResponse = {
          items: response,
          total: response.length,
          page: page,
          size: size,
          pages: Math.ceil(response.length / size),
        };
      } else {
        normalizedResponse = {
          items: [],
          total: 0,
          page: page,
          size: size,
          pages: 0,
        };
      }

      // Normalize each resume object
      normalizedResponse.items = normalizedResponse.items.map((resume: any) => ({
        id: resume.id || resume.resume_id,
        candidateName:
          resume.candidateName || resume.candidate_name || resume.name || 'Unknown Candidate',
        email: resume.candidateEmail || resume.candidate_email || resume.email || '',
        phone: resume.phone || resume.phoneNumber || '',
        jobId: resume.jobId || resume.job_id || '',
        jobTitle: resume.jobTitle || resume.job_title || '',
        status: resume.status || resume.resume_status || 'Pending',
        score: resume.score || null,
        uploadedDate:
          resume.uploadedDate ||
          resume.uploaded_date ||
          resume.createdAt ||
          resume.created_at ||
          new Date().toISOString(),
        assignedSME: resume.assignedSME || resume.assigned_sme || '',
        filePath: resume.filePath || resume.file_path || '',
        fileName: resume.fileName || resume.file_name || '',
        ...resume,
      }));

      return normalizedResponse;
    } catch (error) {
      console.error('Error fetching paginated resumes:', error);
      throw error;
    }
  },

  // Get resumes for a specific job ID
  getResumesByJobId: async (
    jobId: string,
    page: number = 1,
    size: number = 100
  ): Promise<ResumeListApiResponse> => {
    const token = getToken();

    try {
      console.log('Fetching resumes for job ID:', jobId);

      // Build query parameters - using job_id parameter
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());
      queryParams.append('job_id', jobId); // Use job_id parameter

      const url = `${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?${queryParams.toString()}`;
      console.log('Job resumes API URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch job resumes: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Job resumes response:', data);

      // Normalize the response to match ResumeListApiResponse interface
      const normalizedResponse: ResumeListApiResponse = {
        items: (data.items || []).map((resume: any) => ({
          id: resume.resume_id,
          candidateName: resume.candidate_name || 'Unknown Candidate',
          candidateEmail: resume.candidate_email || '',
          email: resume.candidate_email || '',
          phone: resume.phone || '',
          jobId: resume.job_id || '',
          jobTitle: resume.job_title || null,
          status: resume.resume_status || 'pending',
          score: resume.score || 0,
          uploadedDate: resume.createdAt || new Date().toISOString(),
          createdAt: resume.createdAt || new Date().toISOString(),
          assignedSME: resume.assignedSME || '',
          filePath: resume.file_path || '',
          fileName: resume.file_name || '',
          skills: resume.skills || [],
          location: resume.location || '',
          experience_years: resume.experience_years || 0,
          notes: resume.notes || '',
          education: resume.education || '',
          panel_list: resume.panel_list || [],
          updatedAt: resume.updatedAt || '',
          ...resume,
        })),
        total: data.total || 0,
        page: data.page || page,
        size: data.size || size,
        pages: data.pages || Math.ceil((data.total || 0) / size),
      };

      return normalizedResponse;
    } catch (error) {
      console.error('Error fetching job resumes:', error);
      throw error;
    }
  },

  // Get resumes assigned to a specific SME
  getAssignedResumes: async (smeId: string, page = 1, size = 100) => {
    return resumeService.getAllResumes({ assignedSME: smeId, page, size });
  },

  // Create single resume entry 
  createResumeEntry: async (payload: CreateResumePayload): Promise<any> => {
    const token = getToken();

    console.log('Creating resume entry with payload:', payload);

    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.CREATE}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMsg = `Failed to create resume: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              errorMsg = errorData.detail.map((d: any) => d.msg).join(', ');
            } else {
              errorMsg = errorData.detail;
            }
          } else if (errorData.message) {
            errorMsg = errorData.message;
          }
        } catch (e) {
          // Use default error message if JSON parsing fails
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      console.log('Resume entry created:', result);
      return result;
    } catch (error) {
      console.error('Create resume entry failed:', error);
      throw error;
    }
  },

  // ✅ Use bulk upload API for all uploads (individual and bulk)
  uploadResumes: async (
    jobId: string,
    files: File[],
    jobSummary = '',
    jobDescription = '',
    additionalData?: any
  ): Promise<UploadResponse> => {
    const token = getToken();

    console.log(
      'Using bulk upload API for files:',
      files.map(f => f.name)
    );

    try {
      // Create FormData for bulk upload
      const formData = new FormData();

      // Add job details
      formData.append('job_id', jobId);
      formData.append('job_summary', jobSummary || `Bulk upload for job ${jobId}`);
      formData.append('job_description', jobDescription || `Job description for ${jobId}`);

      // Add all files to the form data
      files.forEach((file, index) => {
        formData.append('files', file);
      });

      // Add additional metadata if provided
      if (additionalData) {
        Object.keys(additionalData).forEach(key => {
          formData.append(key, additionalData[key]);
        });
      }

      console.log('Sending bulk upload request...');

      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.BULK_UPLOAD}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMsg = `Bulk upload failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              errorMsg = errorData.detail.map((d: any) => d.msg || d.message || d).join(', ');
            } else {
              errorMsg = errorData.detail;
            }
          } else if (errorData.message) {
            errorMsg = errorData.message;
          }
        } catch (e) {
          console.warn('Could not parse error response');
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      console.log('Bulk upload response:', result);

      return {
        success: result.successful_uploads > 0 || result.total_files > 0,
        message: result.message || `Uploaded ${result.successful_uploads || 0} files successfully`,
        uploaded_files: result.successful_files || [],
        failed_files: result.failed_files || [],
        total_uploaded: result.successful_uploads || 0,
        total_failed: result.failed_uploads || 0,
      };
    } catch (error) {
      console.error('Bulk upload failed:', error);
      throw error;
    }
  },

  // Parse resume data using agent API - Note: This endpoint not in Swagger, keeping as-is
  parseResumeData: async (filename: string) => {
    const token = getToken();

    try {
      console.log('Parsing resume data for:', filename);

      // Use the agent API endpoint with POST method and filename as query parameter
      const url = `http://localhost:8008/api/v1/agent/parseresumedata?filename=${encodeURIComponent(filename)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to parse resume: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Parsed resume data:', data);
      return data;
    } catch (error) {
      console.error('Error parsing resume data:', error);
      throw error;
    }
  },

  // Match resume against job description using agent API - Note: This endpoint not in Swagger, keeping as-is
  matchResume: async (prompt: string[], personalResumeData: any, jobId: string) => {
    const token = getToken();

    try {
      console.log('Matching resume against job:', { prompt, jobId });

      const url = `http://localhost:8008/api/v1/agent/matchresume`;

      const payload = {
        prompt: prompt.reduce(
          (acc, dimension) => {
            acc[dimension] = {};
            return acc;
          },
          {} as Record<string, any>
        ),
        personal_resume_data: personalResumeData || {},
        job_description: {
          job_id: jobId,
        },
      };

      console.log('Match resume payload:', payload);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to match resume: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Match resume response:', data);

      // Extract match_data from the response
      // Response structure: { success, session_id, match_data: {...}, timestamp, ... }
      if (data.match_data) {
        console.log('Extracted match_data:', data.match_data);
        return data.match_data;
      }

      // Fallback: if no match_data field, return the whole response
      return data;
    } catch (error) {
      console.error('Error matching resume:', error);
      throw error;
    }
  },

  // Validate resumes and return scores with optional dimensions - Note: This endpoint not in Swagger, keeping as-is
  validateResumes: async (resumeIds: string[], dimensions?: string[]) => {
    const token = getToken();

    try {
      const requestBody: any = { resume_ids: resumeIds };

      // Include dimensions in the request if provided
      if (dimensions && dimensions.length > 0) {
        requestBody.dimensions = dimensions;
      }

      // Note: This endpoint doesn't exist in the API_ENDPOINTS, keeping hardcoded
      const response = await fetch(`${BASE_URL}/resumes/validate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to validate resumes');
      }

      return response.json();
    } catch (error) {
      console.warn('Resume validation API not available, using mock validation');
      // Fallback to mock validation with dimension-based scoring
      return resumeIds.map(resumeId => {
        const mockResult: any = {
          resumeId,
          score: Math.floor(Math.random() * 40) + 60,
        };

        // If dimensions provided, generate mock scores for each dimension
        if (dimensions && dimensions.length > 0) {
          mockResult.dimensions = dimensions.reduce(
            (acc, dim) => {
              acc[dim] = Math.floor(Math.random() * 40) + 60;
              return acc;
            },
            {} as Record<string, number>
          );
        }

        return mockResult;
      });
    }
  },

  // Get resume by ID
  getResumeById: async (resumeId: string) => {
    const token = getToken();

    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.GET}/${resumeId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch resume details');
    }

    return response.json();
  },

  // Get resume by resume_id query parameter (for Langflow integration)
  getResumeByResumeId: async (
    resumeId: string,
    page: number = 1,
    size: number = 10
  ): Promise<ResumeListApiResponse> => {
    const token = getToken();

    try {
      console.log('[ResumeService] Fetching resume by resume_id:', resumeId);

      // Build query parameters with resume_id
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());
      queryParams.append('resume_id', resumeId);

      const url = `${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?${queryParams.toString()}`;
      console.log('[ResumeService] Resume API URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ResumeService] API Error Response:', errorText);
        throw new Error(
          `Failed to fetch resume: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      console.log('[ResumeService] Resume API response:', data);

      // Normalize response
      const normalizedResponse: ResumeListApiResponse = {
        items: (data.items || []).map((resume: any) => ({
          id: resume.resume_id || resume.id,
          candidateName: resume.candidate_name || resume.candidateName || 'Unknown Candidate',
          candidateEmail: resume.candidate_email || resume.candidateEmail || resume.email || '',
          email: resume.candidate_email || resume.candidateEmail || resume.email || '',
          phone: resume.phone || resume.phoneNumber || '',
          jobId: resume.job_id || resume.jobId || '',
          jobTitle: resume.job_title || resume.jobTitle || null,
          status: resume.resume_status || resume.status || 'pending',
          score: resume.score || 0,
          uploadedDate:
            resume.createdAt ||
            resume.created_at ||
            resume.uploadedDate ||
            new Date().toISOString(),
          assignedSME: resume.assigned_sme || resume.assignedSME || '',
          filePath: resume.file_path || resume.filePath || '',
          fileName: resume.file_name || resume.fileName || '',
          ...resume,
        })),
        total: data.total || data.total_count || (data.items || []).length,
        page: data.page || page,
        size: data.size || size,
        pages:
          data.pages ||
          Math.ceil((data.total || data.total_count || (data.items || []).length) / size),
      };

      return normalizedResponse;
    } catch (error) {
      console.error('[ResumeService] Error fetching resume by resume_id:', error);
      throw error;
    }
  },

  //Get resume preview/content for viewing
  getResumePreview: async (resumeId: string) => {
    const token = getToken();

    try {
      console.log(`Fetching resume preview for: ${resumeId}`);

      const previewUrl = `${BASE_URL}${API_ENDPOINTS.RESUMES.PREVIEW}/${resumeId}`;
      console.log('Preview API URL:', previewUrl);

      const response = await fetch(previewUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/pdf, application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch resume preview: ${response.status} ${response.statusText}`
        );
      }

      // The preview endpoint returns the PDF blob directly
      const blob = await response.blob();

      // Determine file name from Content-Disposition header or use default
      let fileName = `resume_${resumeId}.pdf`;
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      const fileExtension = fileName.toLowerCase().split('.').pop() || 'pdf';
      const blobUrl = URL.createObjectURL(blob);

      console.log('Created preview blob URL:', blobUrl);

      return {
        id: resumeId,
        fileName,
        fileBlob: blob,
        fileUrl: blobUrl,
        fileType: blob.type || 'application/pdf',
        fileExtension,
        candidateName: 'Unknown Candidate', // Will be populated from resume details if needed
      };
    } catch (error) {
      console.error('Error fetching resume preview:', error);
      throw error;
    }
  },

  getAvailablePanelists: async () => {
    const token = getToken();

    try {
      console.log('Fetching available panelists...');

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.USERS.LIST}?page=1&size=100&role=panelist`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch panelists: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Panelists response:', data);

      // Transform the response to match the expected InterviewPanel format
      const panels: InterviewPanel[] = [];

      if (data.users && Array.isArray(data.users)) {
        // Create panels from the panelist users
        data.users.forEach((user: any) => {
          panels.push({
            id: user.username,
            name: `${user.username} (${user.email})`,
            members: [user.username],
          });
        });
      }

      // If no panels found, create a default message
      if (panels.length === 0) {
        panels.push({
          id: 'no-panelists',
          name: 'No panelists available',
          members: [],
        });
      }

      return {
        panels,
        total: data.total_count || 0,
        page: data.page || 1,
        size: data.size || 100,
      };
    } catch (error) {
      console.error('Error fetching available panelists:', error);
      throw error;
    }
  },

  downloadResume: async (resumeId: string, candidateName?: string): Promise<void> => {
    const token = getToken();

    try {
      console.log(`Downloading resume: ${resumeId}`);

      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.DOWNLOAD}/${resumeId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/octet-stream, application/pdf, */*',
        },
      });

      if (response.status === 403) {
        throw new Error('Access denied: You are not authorized to download this resume');
      }

      if (response.status === 400) {
        let errorMessage = 'Bad Request - Invalid resume ID or request format';
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = Array.isArray(errorData.detail)
              ? errorData.detail.map((d: any) => d.msg || d.message || d).join(', ')
              : errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          console.warn('Could not parse error response');
        }
        throw new Error(`Download failed: ${errorMessage}`);
      }

      if (!response.ok) {
        throw new Error(`Failed to download resume: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Determine file extension from content type
      const contentType = response.headers.get('content-type');
      let fileExtension = 'pdf';
      if (contentType) {
        if (contentType.includes('pdf')) {
          fileExtension = 'pdf';
        } else if (contentType.includes('msword')) {
          fileExtension = 'doc';
        } else if (contentType.includes('wordprocessingml')) {
          fileExtension = 'docx';
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const fileName = candidateName
        ? `${candidateName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}_Resume.${fileExtension}`
        : `Resume_${resumeId}.${fileExtension}`;

      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`Successfully downloaded: ${fileName}`);
    } catch (error) {
      console.error('Error downloading resume:', error);
      throw error;
    }
  },

  assignPanelToResume: assignPanelToResumeAPI,

  // Update resume status
  updateResumeStatus: updateResumeStatusAPI,

  // Update resume details
  updateResumeDetails: async (resumeId: string, updates: any) => {
    const token = getToken();

    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.UPDATE}/${resumeId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`Failed to update resume: ${response.status} ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating resume:', error);
      throw error;
    }
  },

  // Delete single resume
  deleteResume: async (resumeId: string) => {
    const token = getToken();

    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.DELETE}/${resumeId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete resume');
    }

    return response.status === 204 ? { success: true } : response.json();
  },

  // Get SMEs 
  getSMEList: async (page: number = 1, size: number = 100): Promise<any> => {
    const token = getToken();

    try {
      console.log('Fetching SME list...');

      const queryParams = new URLSearchParams();
      queryParams.append('role', 'sme');
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());
      queryParams.append('active_only', 'true');

      const url = `${BASE_URL}${API_ENDPOINTS.USERS.LIST}?${queryParams.toString()}`;
      console.log('SME API URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SMEs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('SME list response:', data);

      return data;
    } catch (error) {
      console.error('Error fetching SME list:', error);
      throw error;
    }
  },

  assignSMEToJob: async (jobId: string, smeEmail: string): Promise<any> => {
    const token = getToken();

    try {
      console.log('Assigning SME to job:', jobId, smeEmail);

      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.ASSIGN_SME}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          job_id: jobId,
          sme: smeEmail,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to assign SME: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Assign SME response:', data);

      return data;
    } catch (error) {
      console.error('Error assigning SME:', error);
      throw error;
    }
  },

  // Assign panel to resume and auto-update status
  assignPanelToResumeWithStatus: async (resumeId: string, panelId: string, assignmentData: any) => {
    try {
      // First assign the panel
      const assignResult = await assignPanelToResumeAPI(resumeId, panelId, assignmentData);

      const statusResult = await updateResumeStatusAPI(
        resumeId,
        'interview_scheduled',
        'Panel assigned for interview',
        'Resume assigned to interview panel'
      );

      return {
        assignResult,
        statusResult,
      };
    } catch (error) {
      console.error('Error assigning panel and updating status:', error);
      throw error;
    }
  },
};
